import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStateReducer, showNotification, captureException } from 'r/utils';
import type { Analyse, SanseiEvent } from 'r/types';
import { POST_EVENT_PLAY, fetchApiRaw } from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';
import { validateEventOutcomes } from '../../../Root/App/AdminDashboard/validateEventOutcomes';
import {
  useUpdateEvent,
  useDeleteEvent,
} from '../../../Root/App/AdminDashboard/EventsManagerTab/mutations';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { MEME_URLS, GOOD_MEMES, BAD_MEMES } from 'r/constants/memes';

interface EventOutcome {
  id: string;
  title: string;
  moreThanValue: string | null;
  lessThanValue: string | null;
  moreThanOperator: '>' | '≥';
  lessThanOperator: '<' | '≤';
}

interface EventFormModalProps {
  mode: 'create' | 'edit';
  analysis?: Analyse;
  event?: SanseiEvent;
  isOpen?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function formatOutcomeNumber(value: number): string {
  // Round to 10 significant digits to eliminate 1e-12 floating-point noise,
  // then strip trailing zeros via parseFloat
  return parseFloat(value.toPrecision(10)).toString();
}

function generateOutcomeTitle(outcome: {
  moreThanValue: string | null;
  lessThanValue: string | null;
  moreThanOperator: '>' | '≥';
  lessThanOperator: '<' | '≤';
}): string {
  const parts: string[] = [];

  if (outcome.moreThanValue) {
    const v = parseFloat(outcome.moreThanValue);
    if (!isNaN(v)) {
      const adjusted = outcome.moreThanOperator === '≥' ? v - 1e-12 : v;
      parts.push(
        `${outcome.moreThanOperator} ${formatOutcomeNumber(adjusted)}`,
      );
    }
  }

  if (outcome.lessThanValue) {
    const v = parseFloat(outcome.lessThanValue);
    if (!isNaN(v)) {
      const adjusted = outcome.lessThanOperator === '≤' ? v + 1e-12 : v;
      parts.push(
        `${outcome.lessThanOperator} ${formatOutcomeNumber(adjusted)}`,
      );
    }
  }

  return parts.join(' and ');
}

export function formatDateForDescription(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  const utcHours = date.getUTCHours();
  const ampm = utcHours >= 12 ? 'PM' : 'AM';
  const hours12 = utcHours % 12 || 12;
  return `${day}/${month}/${year} ${hours12} ${ampm} UTC`;
}

export function updateDescriptionLine(
  desc: string,
  prefix: string,
  value: string,
): string {
  const line = `${prefix}: ${value}`;
  const regex = new RegExp(`^${prefix}:.*$`, 'm');
  if (regex.test(desc)) {
    return desc.replace(regex, line);
  }
  return desc ? `${desc}\n${line}` : line;
}

export default function EventFormModal({
  mode,
  analysis,
  event,
  isOpen: isOpenProp = true,
  onClose,
  onSuccess,
}: EventFormModalProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const isClosingRef = useRef(false);
  const descriptionRef = useRef('');
  // Single debounce timer for auto-title updates across all outcomes
  const autoTitleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Maps outcome id → last auto-generated title (to detect manual edits)
  const autoTitlePrevRef = useRef<Record<string, string>>({});
  const queryClient = useQueryClient();
  const { mutateAsync: updateEvent } = useUpdateEvent();
  const { mutateAsync: deleteEvent } = useDeleteEvent();

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  const [
    {
      title,
      description,
      startDate,
      entranceDeadline,
      endDate,
      outcomes,
      imageUrl,
      isSubmitting,
      isDeleting,
    },
    ss,
  ] = useStateReducer({
    title: '',
    description: '',
    startDate: '',
    entranceDeadline: '',
    endDate: '',
    imageUrl: null as string | null,
    outcomes: [
      {
        id: '1',
        title: '',
        moreThanValue: '',
        lessThanValue: '',
        moreThanOperator: '>' as '>' | '≥',
        lessThanOperator: '<' as '<' | '≤',
      },
      {
        id: '2',
        title: '',
        moreThanValue: '',
        lessThanValue: '',
        moreThanOperator: '>' as '>' | '≥',
        lessThanOperator: '<' as '<' | '≤',
      },
    ],
    isSubmitting: false,
    isDeleting: false,
  });

  const canEdit = useMemo(() => {
    if (mode === 'create') return true;
    if (!event) return false;
    const startDateObj = new Date(event.startDate);
    const now = new Date();
    return startDateObj >= now;
  }, [mode, event]);

  const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000;
  const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

  const tweetDate = analysis?.tweetDate ?? event?.tweetDate ?? null;

  // In create mode: if now is more than 4 days after tweetDate, block creation entirely
  const isTweetDateExpired = useMemo(() => {
    if (mode !== 'create' || !tweetDate) return false;
    const tweetDateObj = new Date(tweetDate);
    const now = new Date();
    return now.getTime() - tweetDateObj.getTime() > FOUR_DAYS_MS;
  }, [mode, tweetDate]);

  const areDatesValid = useMemo(() => {
    if (!startDate || !entranceDeadline || !endDate) {
      return false;
    }
    const startDateObj = new Date(startDate);
    const entranceDeadlineObj = new Date(entranceDeadline);
    const endDateObj = new Date(endDate);
    return (
      startDateObj <= entranceDeadlineObj && entranceDeadlineObj <= endDateObj
    );
  }, [startDate, entranceDeadline, endDate]);

  const isStartDateNotPast = useMemo(() => {
    if (!startDate) return false;
    const startDateObj = new Date(startDate);
    const now = new Date();
    return startDateObj >= now;
  }, [startDate]);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && event) {
      const outcomesData = event.eventOutcomes || [];
      const populatedOutcomes = outcomesData.map((outcome, index) => ({
        id: String(index),
        title: outcome.title || '',
        moreThanValue: outcome.moreThanValue || '',
        lessThanValue: outcome.lessThanValue || '',
        moreThanOperator: '>' as '>' | '≥',
        lessThanOperator: '<' as '<' | '≤',
      }));

      ss({
        title: event.title || '',
        description: event.description || '',
        startDate: event.startDate || '',
        entranceDeadline: event.entranceDeadline || '',
        endDate: event.endDate || '',
        imageUrl: event.imageUrl ?? null,
        outcomes:
          populatedOutcomes.length > 0 ?
            populatedOutcomes
          : [
              {
                id: '1',
                title: '',
                moreThanValue: '',
                lessThanValue: '',
                moreThanOperator: '>' as '>' | '≥',
                lessThanOperator: '<' as '<' | '≤',
              },
              {
                id: '2',
                title: '',
                moreThanValue: '',
                lessThanValue: '',
                moreThanOperator: '>' as '>' | '≥',
                lessThanOperator: '<' as '<' | '≤',
              },
            ],
      });
    }
  }, [mode, event, isOpen]);

  // Keep ref in sync so the date→description effect can read current value
  // without adding description to its dependency array (which would loop).
  useEffect(() => {
    descriptionRef.current = description;
  }, [description]);

  const outcomesValuesKey = outcomes
    .map(
      (o: EventOutcome) =>
        `${o.id}:${o.moreThanValue}:${o.lessThanValue}:${o.moreThanOperator}:${o.lessThanOperator}`,
    )
    .join('|');

  // Auto-update outcome titles with debounce 500ms when values/operators change.
  // Only overwrites if the title is empty or matches the last auto-generated value.
  useEffect(() => {
    if (autoTitleTimerRef.current) clearTimeout(autoTitleTimerRef.current);

    autoTitleTimerRef.current = setTimeout(() => {
      ss((prev: { outcomes: EventOutcome[] }) => {
        const updated = prev.outcomes.map((outcome: EventOutcome) => {
          const generated = generateOutcomeTitle(outcome);
          const lastAuto = autoTitlePrevRef.current[outcome.id] ?? '';
          const isAutoOrEmpty =
            outcome.title === '' || outcome.title === lastAuto;
          if (!isAutoOrEmpty) return outcome;
          autoTitlePrevRef.current[outcome.id] = generated;
          return { ...outcome, title: generated };
        });
        return { outcomes: updated };
      });
    }, 500);
  }, [outcomesValuesKey]);

  // Auto-update "Entry Deadline" and "Settlement date" lines in description
  // whenever the corresponding date fields change.
  useEffect(() => {
    if (!entranceDeadline && !endDate) return;

    let newDesc = descriptionRef.current;

    if (entranceDeadline) {
      newDesc = updateDescriptionLine(
        newDesc,
        'Entry Deadline',
        formatDateForDescription(entranceDeadline),
      );
    }

    if (endDate) {
      newDesc = updateDescriptionLine(
        newDesc,
        'Settlement date',
        formatDateForDescription(endDate),
      );
    }

    if (newDesc !== descriptionRef.current) {
      ss({ description: newDesc });
    }
  }, [entranceDeadline, endDate]);

  const formatDateWith00Seconds = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    date.setSeconds(0, 0);
    return date.toISOString();
  };

  const normalizeOperator = (op: string) => {
    if (op === '≥' || op === '>=') return '≥';
    if (op === '≤' || op === '<=') return '≤';
    return op;
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    if (mode === 'create') {
      if (!title.trim()) errors.push('Title is required');
      if (!description.trim()) errors.push('Description is required');
      if (!analysis) errors.push('Analysis data is missing');
    }

    if (outcomes.length < 2) errors.push('At least 2 outcomes are required');

    if (!startDate) errors.push('Start date is required');
    if (!entranceDeadline) errors.push('Entrance deadline is required');
    if (!endDate) errors.push('End date is required');

    if (
      startDate &&
      entranceDeadline &&
      new Date(startDate) > new Date(entranceDeadline)
    )
      errors.push('Start date must be before entrance deadline');
    if (
      entranceDeadline &&
      endDate &&
      new Date(entranceDeadline) > new Date(endDate)
    )
      errors.push('Entrance deadline must be before end date');

    if (mode === 'create' && startDate && !isStartDateNotPast)
      errors.push('Start date must not be in the past');

    // startDate must be within [tweetDate, tweetDate + 4 days] in create mode
    if (mode === 'create' && startDate && tweetDate) {
      const tweetDateObj = new Date(tweetDate);
      const startDateObj = new Date(startDate);
      if (startDateObj < tweetDateObj)
        errors.push('Start date cannot be before the tweet date');
      else if (startDateObj.getTime() - tweetDateObj.getTime() > FOUR_DAYS_MS)
        errors.push(
          'Start date cannot be more than 4 days after the tweet date',
        );
    }

    // endDate must be within 2 weeks of startDate
    if (startDate && endDate) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      if (endDateObj.getTime() - startDateObj.getTime() > TWO_WEEKS_MS)
        errors.push('End date cannot be more than 2 weeks after start date');
    }

    // Per-outcome value checks
    const outcomeValueErrors: string[] = [];
    outcomes.forEach((outcome: EventOutcome, index: number) => {
      const num = index + 1;

      if (mode === 'create') {
        if (!outcome.title.trim())
          outcomeValueErrors.push(`Outcome ${num}: title is required`);
        if (!outcome.moreThanValue && !outcome.lessThanValue)
          outcomeValueErrors.push(
            `Outcome ${num}: at least one value is required`,
          );
      }

      if (outcome.moreThanValue && outcome.lessThanValue) {
        const mtv = parseFloat(outcome.moreThanValue);
        const ltv = parseFloat(outcome.lessThanValue);
        if (!isNaN(mtv) && !isNaN(ltv)) {
          const mo = outcome.moreThanOperator;
          const lo = outcome.lessThanOperator;
          const invalid = mo === '≥' && lo === '≤' ? mtv > ltv : mtv >= ltv;
          if (invalid)
            outcomeValueErrors.push(
              `Outcome ${num}: "more than" value must be less than "less than" value`,
            );
        }
      }
    });
    errors.push(...outcomeValueErrors);

    // Range structure validation (gaps, -∞/+∞ boundaries) — only when
    // per-outcome values are valid to avoid duplicate errors
    if (outcomeValueErrors.length === 0 && outcomes.length >= 2) {
      try {
        const processed = outcomes.map((outcome: EventOutcome) => {
          let moreThanValue = outcome.moreThanValue || null;
          let lessThanValue = outcome.lessThanValue || null;
          const mo = outcome.moreThanOperator;
          const lo = outcome.lessThanOperator;
          if (mo === '≥' && moreThanValue) {
            const v = parseFloat(moreThanValue);
            if (!isNaN(v)) moreThanValue = (v - 1e-12).toFixed(12);
          }
          if (lo === '≤' && lessThanValue) {
            const v = parseFloat(lessThanValue);
            if (!isNaN(v)) lessThanValue = (v + 1e-12).toFixed(12);
          }
          return {
            title: outcome.title || `Outcome`,
            moreThanValue,
            lessThanValue,
          };
        });
        validateEventOutcomes(processed);
      } catch (e) {
        const msg = (e as Error).message;
        if (msg.includes('start from -Infinity'))
          errors.push(
            'One outcome must have no lower bound (no "more than" value)',
          );
        else if (msg.includes('end with Infinity'))
          errors.push(
            'One outcome must have no upper bound (no "less than" value)',
          );
        else errors.push(msg);
      }
    }

    return errors;
  }, [
    mode,
    title,
    description,
    analysis,
    startDate,
    entranceDeadline,
    endDate,
    isStartDateNotPast,
    tweetDate,
    outcomes,
  ]);

  const validateOutcomes = useCallback(() => {
    const invalidOutcomes = outcomes.filter((outcome: EventOutcome) => {
      if (!outcome.moreThanValue && !outcome.lessThanValue) {
        return false;
      }

      const moreThanValue =
        outcome.moreThanValue ? parseFloat(outcome.moreThanValue) : NaN;
      const lessThanValue =
        outcome.lessThanValue ? parseFloat(outcome.lessThanValue) : NaN;

      if (
        (outcome.moreThanValue && isNaN(moreThanValue)) ||
        (outcome.lessThanValue && isNaN(lessThanValue))
      ) {
        return true;
      }

      if (!isNaN(moreThanValue) && !isNaN(lessThanValue)) {
        const moreThanOperator = normalizeOperator(outcome.moreThanOperator);
        const lessThanOperator = normalizeOperator(outcome.lessThanOperator);

        if (moreThanOperator === '≥' && lessThanOperator === '≤') {
          return moreThanValue > lessThanValue;
        } else if (moreThanOperator === '>' && lessThanOperator === '≤') {
          return moreThanValue >= lessThanValue;
        } else if (moreThanOperator === '≥' && lessThanOperator === '<') {
          return moreThanValue >= lessThanValue;
        } else if (moreThanOperator === '>' && lessThanOperator === '<') {
          return moreThanValue >= lessThanValue;
        }
      }

      return false;
    });

    return invalidOutcomes.length === 0;
  }, [outcomes]);

  const processOutcomes = useCallback(() => {
    return outcomes.map((outcome: EventOutcome) => {
      let moreThanValue = outcome.moreThanValue;
      let lessThanValue = outcome.lessThanValue;

      const normalizedMoreThanOperator = normalizeOperator(
        outcome.moreThanOperator,
      );
      const normalizedLessThanOperator = normalizeOperator(
        outcome.lessThanOperator,
      );

      if (normalizedMoreThanOperator === '≥' && moreThanValue) {
        const value = parseFloat(moreThanValue);
        if (!isNaN(value)) {
          moreThanValue = (value - parseFloat((1e-12).toFixed(12))).toFixed(12);
        }
      }

      if (normalizedLessThanOperator === '≤' && lessThanValue) {
        const value = parseFloat(lessThanValue);
        if (!isNaN(value)) {
          lessThanValue = (value + parseFloat((1e-12).toFixed(12))).toFixed(12);
        }
      }

      return {
        title: outcome.title,
        moreThanValue: moreThanValue || null,
        lessThanValue: lessThanValue || null,
      };
    });
  }, [outcomes]);

  const handleSubmit = useCallback(async () => {
    const accessTokens = StoredAuth.getAll();
    if (!accessTokens) {
      showNotification('error', 'Please check token');
      return;
    }

    if (!title.trim() || !description.trim()) {
      showNotification(
        'error',
        'Please fill all required fields (title and description)',
      );
      return;
    }

    if (outcomes.length < 2) {
      showNotification('error', 'Please add at least 2 outcomes');
      return;
    }

    const invalidOutcomeTitles = outcomes.filter(
      (out: EventOutcome) =>
        !out.title.trim() || (!out.moreThanValue && !out.lessThanValue),
    );
    if (invalidOutcomeTitles.length > 0) {
      showNotification(
        'error',
        'Please fill all outcome titles and provide at least one value for each outcome',
      );
      return;
    }

    if (isTweetDateExpired) {
      showNotification(
        'error',
        'Cannot create event: more than 4 days have passed since the tweet date',
      );
      return;
    }

    if (!areDatesValid) {
      showNotification(
        'error',
        'Invalid date order: startDate must be before entranceDeadline, which must be before endDate',
      );
      return;
    }
    if (!isStartDateNotPast) {
      showNotification(
        'error',
        'Start date must not be earlier than current date',
      );
      return;
    }
    if (!entranceDeadline.trim()) {
      showNotification('error', 'Entrance Deadline is required');
      return;
    }

    if (!validateOutcomes()) {
      showNotification(
        'error',
        'Invalid outcome values: More than value must be less than or equal to less than value',
      );
      return;
    }

    try {
      validateEventOutcomes(
        outcomes.map((o: EventOutcome) => ({
          title: o.title,
          moreThanValue: o.moreThanValue || null,
          lessThanValue: o.lessThanValue || null,
        })),
      );
    } catch (e) {
      showNotification('error', (e as Error).message);
      return;
    }

    if (!analysis) {
      showNotification('error', 'Analysis data is required');
      return;
    }

    ss({ isSubmitting: true });
    try {
      const processedOutcomes = processOutcomes();

      const body = {
        kolID: analysis.authorID,
        analysisId: analysis.id,
        isDirectionUp: analysis.isPredictionUp,
        tweetText: analysis.tweetText,
        tweetImages: analysis.images,
        tweetDate: analysis.tweetDate,
        imageUrl: imageUrl || null,
        kolAvatar: analysis.authorIcon || null,
        kolUsername: analysis.authorUsername || null,
        kolName: analysis.authorName || null,
        title: title,
        description: description,
        tweetID: analysis.tweetID,
        startDate:
          formatDateWith00Seconds(startDate) || new Date().toISOString(),
        endDate: formatDateWith00Seconds(endDate) || new Date().toISOString(),
        entranceDeadline:
          entranceDeadline ? formatDateWith00Seconds(entranceDeadline) : null,
        resolutionCondition: 'expired',
        symbol: analysis.pair,
        eventOutcomes: processedOutcomes,
      };
      await fetchApiRaw(POST_EVENT_PLAY, { body }, accessTokens.cms);
      queryClient.invalidateQueries({ queryKey: ['cms-events-play'] });

      showNotification('success', 'Play event created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      captureException(error);
      showNotification('error', 'Failed to create Play event');
    } finally {
      ss({ isSubmitting: false });
    }
  }, [
    analysis,
    title,
    description,
    startDate,
    endDate,
    entranceDeadline,
    imageUrl,
    outcomes,
    areDatesValid,
    isStartDateNotPast,
    isTweetDateExpired,
    validateOutcomes,
    processOutcomes,
    onSuccess,
    onClose,
    queryClient,
  ]);

  const handleUpdate = useCallback(async () => {
    if (!event) return;

    if (!validateOutcomes()) {
      showNotification(
        'error',
        'Invalid outcome values: More than value must be less than or equal to less than value',
      );
      return;
    }

    try {
      validateEventOutcomes(
        outcomes.map((o: EventOutcome) => ({
          title: o.title,
          moreThanValue: o.moreThanValue || null,
          lessThanValue: o.lessThanValue || null,
        })),
      );
    } catch (e) {
      showNotification('error', (e as Error).message);
      return;
    }

    ss({ isSubmitting: true });
    try {
      const processedOutcomes = processOutcomes();

      await updateEvent({
        eventId: event.id,
        body: {
          title,
          description,
          pair: event.symbol,
          startDate,
          entranceDeadline,
          endDate,
          imageUrl: imageUrl || null,
          eventOutcomes: processedOutcomes.map(
            (
              outcome: {
                title: string;
                moreThanValue: string | null;
                lessThanValue: string | null;
              },
              index: number,
            ) => ({
              ...outcome,
              voteCount: event.eventOutcomes[index]?.voteCount || '0',
            }),
          ),
        },
      });

      // showNotification('success', 'Event updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      captureException(error);
      showNotification('error', 'Failed to update event');
    } finally {
      ss({ isSubmitting: false });
    }
  }, [
    event,
    title,
    description,
    startDate,
    entranceDeadline,
    endDate,
    imageUrl,
    outcomes,
    validateOutcomes,
    processOutcomes,
    updateEvent,
    onSuccess,
    onClose,
  ]);

  const handleDelete = useCallback(async () => {
    if (!event) return;

    if (!isDeleting) {
      ss({ isDeleting: true });
      return;
    }

    try {
      await deleteEvent(event.id);
      onSuccess();
      onClose();
    } catch (error) {
      captureException(error);
      showNotification('error', 'Failed to delete event');
      ss({ isDeleting: false });
    }
  }, [event, isDeleting, deleteEvent, onSuccess, onClose]);

  const handleAddOutcome = () => {
    const newId = String(Date.now());
    ss({
      outcomes: [
        ...outcomes,
        {
          id: newId,
          title: '',
          moreThanValue: '',
          lessThanValue: '',
          moreThanOperator: '>' as '>' | '≥',
          lessThanOperator: '<' as '<' | '≤',
        },
      ],
    });
  };

  const handleRemoveOutcome = (outcomeId: string) => {
    if (outcomes.length <= 2) {
      showNotification('error', 'At least 2 outcomes are required');
      return;
    }
    ss({
      outcomes: outcomes.filter((out: EventOutcome) => out.id !== outcomeId),
    });
  };

  const handleOutcomeChange = (
    outcomeId: string,
    field: keyof EventOutcome,
    value: string,
  ) => {
    ss({
      outcomes: outcomes.map((out: EventOutcome) =>
        out.id === outcomeId ? { ...out, [field]: value } : out,
      ),
    });
  };

  const addMinutes = (date: Date, minutes: number): Date =>
    new Date(date.getTime() + minutes * 60 * 1000);

  // Default start = max(now, tweetDate) + 5 min, clamped to tweetDate + 4 days
  const handleStartDateOpen = useCallback(() => {
    if (mode !== 'create' || startDate) return;
    const now = new Date();
    const base =
      tweetDate && new Date(tweetDate) > now ? new Date(tweetDate) : now;
    let def = addMinutes(base, 5);
    if (tweetDate) {
      const maxAllowed = new Date(new Date(tweetDate).getTime() + FOUR_DAYS_MS);
      if (def > maxAllowed) def = maxAllowed;
    }
    ss({ startDate: def.toISOString() });
  }, [mode, startDate, tweetDate]);

  const handleEntranceDeadlineOpen = useCallback(() => {
    if (mode !== 'create' || entranceDeadline) return;
    const base = startDate ? new Date(startDate) : new Date();
    ss({ entranceDeadline: addMinutes(base, 5).toISOString() });
  }, [mode, entranceDeadline, startDate]);

  const handleEndDateOpen = useCallback(() => {
    if (mode !== 'create' || endDate) return;
    const base =
      entranceDeadline ? new Date(entranceDeadline)
      : startDate ? new Date(startDate)
      : new Date();
    ss({ endDate: addMinutes(base, 5).toISOString() });
  }, [mode, endDate, entranceDeadline, startDate]);

  // Cascading date change handlers
  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        ss({ startDate: '' });
        return;
      }
      const updates: {
        startDate: string;
        entranceDeadline?: string;
        endDate?: string;
      } = { startDate: date.toISOString() };

      // Clamp endDate: must not exceed startDate + 2 weeks
      let effectiveEnd = endDate ? new Date(endDate) : null;
      if (effectiveEnd) {
        if (effectiveEnd < date) {
          effectiveEnd = addMinutes(date, 30);
          updates.endDate = effectiveEnd.toISOString();
        } else if (effectiveEnd.getTime() - date.getTime() > TWO_WEEKS_MS) {
          effectiveEnd = new Date(date.getTime() + TWO_WEEKS_MS);
          updates.endDate = effectiveEnd.toISOString();
        }
      }

      // Clamp entranceDeadline into [startDate, endDate]
      if (entranceDeadline) {
        const dl = new Date(entranceDeadline);
        if (dl < date) {
          updates.entranceDeadline = addMinutes(date, 5).toISOString();
        } else if (effectiveEnd && dl > effectiveEnd) {
          updates.entranceDeadline = effectiveEnd.toISOString();
        }
      }

      ss(updates);
    },
    [endDate, entranceDeadline],
  );

  const handleEntranceDeadlineChange = useCallback((date: Date | undefined) => {
    ss({ entranceDeadline: date ? date.toISOString() : '' });
  }, []);

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        ss({ endDate: '' });
        return;
      }
      const updates: { endDate: string; entranceDeadline?: string } = {
        endDate: date.toISOString(),
      };

      // Pull entranceDeadline back if it exceeds new endDate
      if (entranceDeadline && new Date(entranceDeadline) > date) {
        updates.entranceDeadline = date.toISOString();
      }

      ss(updates);
    },
    [entranceDeadline],
  );

  const handleClose = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsOpen(false);
    onClose();
  };

  const renderHeader = () => {
    const data = analysis ?? event;
    if (!data) return null;

    const authorName = analysis?.authorName ?? event?.kolUsername ?? '';
    const authorUsername = analysis?.authorUsername;
    const symbol = analysis?.pair ?? event?.symbol ?? '';
    const direction = analysis?.isPredictionUp ?? event?.isDirectionUp;
    const tweetText = analysis?.tweetText ?? event?.tweetText ?? null;
    const tweetDate = analysis?.tweetDate ?? event?.tweetDate ?? null;
    const images = analysis?.images ?? event?.tweetImages ?? null;

    return (
      <div className="flex flex-col gap-2 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-3">
          <div>
            <p className="font-medium">{authorName}</p>
            {authorUsername && (
              <p className="text-sm text-(--textSecondary)">
                @{authorUsername}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-2 py-1 text-xs bg-accent rounded">{symbol}</span>
          {direction !== undefined && direction !== null && (
            <span className="px-2 py-1 text-xs bg-accent rounded">
              {direction ? 'Up' : 'Down'}
            </span>
          )}
        </div>
        {tweetText && (
          <p className="text-sm whitespace-pre-wrap">{tweetText}</p>
        )}
        {tweetDate && (
          <p className="text-xs text-(--textSecondary)">
            Tweet: {new Date(tweetDate).toLocaleString()}
          </p>
        )}
        {images?.[0] && (
          <div className="text-sm text-(--textSecondary)">
            <p>Image URL:</p>
            <p className="truncate">{images[0]}</p>
          </div>
        )}
      </div>
    );
  };

  if (mode === 'edit' && !event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="text-lg font-semibold">
          {mode === 'create' ?
            'Add Play Event'
          : !canEdit ?
            'Event started and cannot be edited'
          : 'Edit Event'}
        </DialogTitle>

        <div className="flex flex-col gap-6 mt-4">
          {renderHeader()}

          {mode === 'create' && isTweetDateExpired && (
            <div className="rounded-md bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 text-sm text-yellow-600 dark:text-yellow-400">
              <p className="font-medium">Cannot create event</p>
              <p className="mt-1">
                More than 4 days have passed since the tweet date (
                {tweetDate ? new Date(tweetDate).toLocaleString() : '—'}).
                Events can only be created within 4 days of the tweet.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-bold uppercase text-(--textSecondary)">
              Event Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => ss({ title: e.target.value })}
                placeholder="Enter event title"
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => ss({ description: e.target.value })}
                placeholder="Enter event description"
                disabled={!canEdit}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>
                  Start Date <span className="text-red-500">*</span>
                </Label>
                <DateTimePicker
                  value={startDate ? new Date(startDate) : undefined}
                  onChange={handleStartDateChange}
                  onOpen={handleStartDateOpen}
                  minDate={
                    mode === 'create' && tweetDate ?
                      new Date(tweetDate)
                    : undefined
                  }
                  maxDate={
                    mode === 'create' && tweetDate ?
                      new Date(new Date(tweetDate).getTime() + FOUR_DAYS_MS)
                    : endDate ?
                      new Date(endDate)
                    : undefined
                  }
                  disabled={!canEdit}
                  placeholder="Pick start date & time"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Entrance Deadline <span className="text-red-500">*</span>
                </Label>
                <DateTimePicker
                  value={
                    entranceDeadline ? new Date(entranceDeadline) : undefined
                  }
                  onChange={handleEntranceDeadlineChange}
                  onOpen={handleEntranceDeadlineOpen}
                  minDate={startDate ? new Date(startDate) : undefined}
                  maxDate={endDate ? new Date(endDate) : undefined}
                  disabled={!canEdit}
                  placeholder="Pick deadline & time"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  End Date <span className="text-red-500">*</span>
                </Label>
                <DateTimePicker
                  value={endDate ? new Date(endDate) : undefined}
                  onChange={handleEndDateChange}
                  onOpen={handleEndDateOpen}
                  minDate={startDate ? new Date(startDate) : undefined}
                  maxDate={
                    startDate ?
                      new Date(new Date(startDate).getTime() + TWO_WEEKS_MS)
                    : undefined
                  }
                  disabled={!canEdit}
                  placeholder="Pick end date & time"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-bold uppercase text-(--textSecondary)">
              Meme
            </h3>
            {[GOOD_MEMES, BAD_MEMES].map((row, rowIdx) => (
              <div key={rowIdx} className="grid grid-cols-7 gap-2">
                {row.map((name) => {
                  const isSelected = imageUrl === MEME_URLS[name];
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() =>
                        ss({ imageUrl: isSelected ? null : MEME_URLS[name] })
                      }
                      disabled={!canEdit}
                      className={`rounded border-2 overflow-hidden transition-all ${
                        isSelected ?
                          'border-blue-500 opacity-100'
                        : 'border-transparent opacity-40 hover:opacity-70'
                      }`}
                    >
                      <img
                        src={MEME_URLS[name]}
                        alt={name}
                        className="w-full aspect-square object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            ))}
            {imageUrl && Object.values(MEME_URLS).includes(imageUrl) && (
              <button
                type="button"
                onClick={() => ss({ imageUrl: null })}
                className="text-xs text-muted-foreground hover:text-foreground self-start underline"
              >
                Clear meme
              </button>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold uppercase text-(--textSecondary)">
                Event Outcomes
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddOutcome}
                disabled={!canEdit}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Outcome
              </Button>
            </div>

            <div className="flex flex-col gap-4">
              {outcomes.map((outcome: EventOutcome, index: number) => (
                <div
                  key={outcome.id}
                  className={`flex flex-col gap-3 p-4 border rounded-lg ${
                    index > 1 ?
                      'border-l-4 border-l-red-500 border-border'
                    : 'border-border'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Outcome {index + 1}</span>
                    {outcomes.length > 2 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveOutcome(outcome.id)}
                        disabled={!canEdit}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`outcome-title-${outcome.id}`}>
                      Outcome Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`outcome-title-${outcome.id}`}
                      value={outcome.title}
                      onChange={(e) =>
                        handleOutcomeChange(outcome.id, 'title', e.target.value)
                      }
                      placeholder="e.g., Price exceeds threshold"
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>More Than Operator</Label>
                      <Select
                        value={outcome.moreThanOperator}
                        onValueChange={(value) =>
                          handleOutcomeChange(
                            outcome.id,
                            'moreThanOperator',
                            value,
                          )
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value=">">&gt;</SelectItem>
                          <SelectItem value="≥">≥</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Less Than Operator</Label>
                      <Select
                        value={outcome.lessThanOperator}
                        onValueChange={(value) =>
                          handleOutcomeChange(
                            outcome.id,
                            'lessThanOperator',
                            value,
                          )
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="<">&lt;</SelectItem>
                          <SelectItem value="≤">≤</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>More Than Value (optional)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={outcome.moreThanValue || ''}
                        onChange={(e) =>
                          handleOutcomeChange(
                            outcome.id,
                            'moreThanValue',
                            e.target.value,
                          )
                        }
                        placeholder="0.00"
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Less Than Value (optional)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={outcome.lessThanValue || ''}
                        onChange={(e) =>
                          handleOutcomeChange(
                            outcome.id,
                            'lessThanValue',
                            e.target.value,
                          )
                        }
                        placeholder="0.00"
                        disabled={!canEdit}
                      />
                    </div>
                  </div>

                  <div className="text-sm text-(--textSecondary)">
                    <p>
                      Conditions:{' '}
                      {outcome.moreThanValue && (
                        <span>
                          {outcome.moreThanOperator} {outcome.moreThanValue}
                          {outcome.moreThanOperator === '≥' &&
                            ' (adjusted by -1×10⁻¹²)'}
                        </span>
                      )}
                      {outcome.moreThanValue &&
                        outcome.lessThanValue &&
                        ' and '}
                      {outcome.lessThanValue && (
                        <span>
                          {outcome.lessThanOperator} {outcome.lessThanValue}
                          {outcome.lessThanOperator === '≤' &&
                            ' (adjusted by +1×10⁻¹²)'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs mt-1">
                      Note: For "≥" and "≤" operators, values are adjusted by
                      ±1×10⁻¹²
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {outcomes.length < 2 && (
              <p className="text-sm text-(--textSecondary)">
                At least 2 outcomes are required
              </p>
            )}
          </div>

          {mode === 'edit' && canEdit && (
            <div>
              <h3 className="font-semibold mb-2 text-red-500">Danger Zone</h3>
              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={!canEdit}
                >
                  {isDeleting ?
                    'Click again to confirm DELETE'
                  : 'DELETE EVENT'}
                </Button>
                {isDeleting && (
                  <Button
                    variant="outline"
                    onClick={() => ss({ isDeleting: false })}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {validationErrors.length > 0 && canEdit && (
              <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2">
                <ul className="text-sm text-red-500 space-y-0.5">
                  {validationErrors.map((err, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{err}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end items-center gap-3">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={mode === 'create' ? handleSubmit : handleUpdate}
                disabled={
                  isSubmitting ||
                  isDeleting ||
                  !canEdit ||
                  !areDatesValid ||
                  !entranceDeadline ||
                  isTweetDateExpired
                }
              >
                {mode === 'create' ?
                  isSubmitting ?
                    'Creating...'
                  : 'Create Event'
                : isSubmitting ?
                  'Saving...'
                : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
